import { alpha, Stack } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ContactReferenceType } from '@/api/address-book/types';
import { _getBffContacts } from '@/api/address-book-bff/queries';
import type { ContactView } from '@/api/address-book-bff/types';
import { _getAdminViewsUsers, _getGammaUserByEmail, _getGammaUserById } from '@/api/admin/queries';
import { AdminDashboardUserStatus } from '@/api/admin/types';
import { _getInvestorEligibility } from '@/api/introducer-dashboard/queries';
import WarningIcon from '@/assets/svg/ui-elements/alert-triangle-yellow.svg';
import InfoCircleIcon from '@/assets/svg/ui-elements/info-circle-alt.svg';
import { EMAIL_REGEX } from '@/constants/static';
import useDebounce from '@/global-hooks/use-debounce';
import {
  Avatar,
  Badge,
  Button,
  Input,
  Radio,
  RadioGroup,
  theme,
  Typography,
} from '@/shared/design-system';

import { StepProgress } from './step-progress';
import {
  CardContainer,
  FooterButtons,
  InfoBanner,
  SearchOptionButton,
  StepContainer,
} from './styled';
import type { AttributionTypeSelection, SelectedIntroducer, SelectedUser } from './types';

interface StepSelectUserProps {
  introducer: SelectedIntroducer;
  selectedUser: SelectedUser | null;
  attributionType: AttributionTypeSelection;
  onSelectUser: (_user: SelectedUser | null) => void;
  onSelectAttributionType: (_type: AttributionTypeSelection) => void;
  onNext: () => void;
  onBack: () => void;
}

export const StepSelectUser = ({
  introducer,
  selectedUser,
  attributionType,
  onSelectUser,
  onSelectAttributionType,
  onNext,
  onBack,
}: StepSelectUserProps) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(selectedUser?.email ?? '');
  const [isSelectingContact, setIsSelectingContact] = useState(false);
  const [contactSelectionError, setContactSelectionError] = useState(false);
  const debouncedSearch = useDebounce(search, 500);
  const normalizedSearch = debouncedSearch.trim();

  const { data, isPending } = useQuery({
    ..._getAdminViewsUsers({ search: normalizedSearch, limit: 10 }),
    enabled: normalizedSearch.length >= 2,
  });
  const {
    data: contactsData,
    isPending: contactsPending,
    isError: contactsError,
  } = useQuery({
    ..._getBffContacts({ search: normalizedSearch, limit: 10 }),
    enabled: normalizedSearch.length >= 2 && !selectedUser,
  });

  const { data: eligibility } = useQuery(
    _getInvestorEligibility(
      selectedUser && !selectedUser.isNew ? (selectedUser.id ?? undefined) : undefined,
    ),
  );

  const users = data?.items ?? [];
  const registeredUserEmails = new Set(
    users
      .filter((user) => !user.statuses.includes(AdminDashboardUserStatus.Pending))
      .map((user) => user.email.trim().toLowerCase()),
  );
  const contacts = (contactsData?.contacts ?? []).filter(
    (contact) =>
      (contact.referenceType === ContactReferenceType.Individual ||
        contact.referenceType === ContactReferenceType.External) &&
      !!contact.email &&
      EMAIL_REGEX.test(contact.email.trim()) &&
      !registeredUserEmails.has(contact.email.trim().toLowerCase()),
  );
  const contactEmails = new Set(contacts.map((contact) => contact.email?.trim().toLowerCase()));
  const visibleUsers = users.filter(
    (user) =>
      !user.statuses.includes(AdminDashboardUserStatus.Pending) ||
      !contactEmails.has(user.email.trim().toLowerCase()),
  );
  const showDropdown = normalizedSearch.length >= 2 && !selectedUser;

  const handleSelectExisting = (user: (typeof users)[0]) => {
    const isNew = user.statuses.includes(AdminDashboardUserStatus.Pending);
    onSelectUser({
      id: isNew ? null : user.id,
      name: user.name,
      email: user.email.trim().toLowerCase(),
      isNew,
      currentIntroducer: isNew ? null : (user.currentIntroducer?.introducerName ?? null),
      avatarUrl: user.avatarUrl,
    });
    setSearch(user.email);
  };

  const handleSelectContact = async (contact: ContactView) => {
    const email = contact.email?.trim().toLowerCase();
    if (!email || !EMAIL_REGEX.test(email)) return;
    setContactSelectionError(false);

    let matchedUser = users.find(
      (user) =>
        user.email.trim().toLowerCase() === email &&
        !user.statuses.includes(AdminDashboardUserStatus.Pending),
    );
    setIsSelectingContact(true);
    try {
      const linkedUserId =
        contact.referenceType === ContactReferenceType.Individual ? contact.referenceId : null;
      const account = linkedUserId
        ? matchedUser?.id === linkedUserId
          ? matchedUser
          : await queryClient.fetchQuery(_getGammaUserById(linkedUserId))
        : (matchedUser ?? (await queryClient.fetchQuery(_getGammaUserByEmail(email))));
      const selectedEmail = account?.email.trim().toLowerCase() ?? email;

      if (account && matchedUser?.id !== account.id) {
        const result = await queryClient.fetchQuery(
          _getAdminViewsUsers({ search: selectedEmail, limit: 10 }),
        );
        matchedUser = result.items.find(
          (user) =>
            user.id === account.id && !user.statuses.includes(AdminDashboardUserStatus.Pending),
        );
      }

      onSelectUser({
        id: account?.id ?? null,
        name: contact.name,
        email: selectedEmail,
        isNew: !account,
        currentIntroducer:
          matchedUser?.id === account?.id
            ? (matchedUser?.currentIntroducer?.introducerName ?? null)
            : null,
        avatarUrl: contact.iconUrl,
        fromAddressBook: true,
      });
      setSearch(selectedEmail);
    } catch {
      setContactSelectionError(true);
    } finally {
      setIsSelectingContact(false);
    }
  };

  const handleEmailInput = (value: string) => {
    setSearch(value);
    setContactSelectionError(false);
    if (selectedUser) {
      onSelectUser(null);
    }
  };

  const handleSelectNewUser = () => {
    if (EMAIL_REGEX.test(search)) {
      onSelectUser({
        id: null,
        name: null,
        email: search,
        isNew: true,
        currentIntroducer: null,
        avatarUrl: null,
      });
    }
  };

  const isValidEmail = EMAIL_REGEX.test(search);

  return (
    <StepContainer>
      <StepProgress currentStep={2} />

      <CardContainer sx={{ padding: '12px 16px' }}>
        <Stack flexDirection="row" alignItems="center" gap="8px">
          <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
            Introducer:
          </Typography>
          <Typography variant="body3" fontWeight={500}>
            {introducer.name}
          </Typography>
        </Stack>
      </CardContainer>

      <Stack gap="8px" flexDirection="column">
        <Typography variant="body3" fontWeight={500}>
          User (investor)
        </Typography>
        <Input
          placeholder="Search for user by name or email address"
          value={search}
          disabled={isSelectingContact}
          onChange={(e) => handleEmailInput(e.target.value)}
        />
      </Stack>

      {showDropdown && (
        <CardContainer sx={{ maxHeight: '200px', overflowY: 'auto' }}>
          {isPending || contactsPending ? (
            <Typography variant="body2" color={alpha(theme.colors.text.primary, 0.6)}>
              Searching...
            </Typography>
          ) : (
            <Stack gap="4px" flexDirection="column">
              {visibleUsers.map((user) => (
                <SearchOptionButton
                  key={user.id}
                  type="button"
                  disabled={isSelectingContact}
                  onClick={() => handleSelectExisting(user)}
                >
                  <Avatar initials={user.name ?? user.email} src={user.avatarUrl} />
                  <Stack flexDirection="column" minWidth={0}>
                    <Typography variant="body2">{user.name ?? '-'}</Typography>
                    <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
                      {user.email}
                    </Typography>
                  </Stack>
                </SearchOptionButton>
              ))}
              {contacts.length > 0 && (
                <Typography variant="body4" color={theme.colors.text.secondary}>
                  Gamma address book
                </Typography>
              )}
              {contacts.map((contact) => (
                <SearchOptionButton
                  key={contact.id}
                  type="button"
                  disabled={isSelectingContact}
                  onClick={() => handleSelectContact(contact)}
                >
                  <Avatar initials={contact.name ?? contact.email ?? ''} src={contact.iconUrl} />
                  <Stack flexDirection="column" minWidth={0}>
                    <Typography variant="body2">{contact.name ?? contact.email}</Typography>
                    {contact.name && (
                      <Typography variant="body3" color={theme.colors.text.secondary}>
                        {contact.email}
                      </Typography>
                    )}
                  </Stack>
                </SearchOptionButton>
              ))}
              {contactsError && (
                <Typography variant="body3" color={theme.colors.text.error}>
                  Could not search Gamma address book contacts.
                </Typography>
              )}
              {isSelectingContact && (
                <Typography variant="body3" color={theme.colors.text.secondary}>
                  Checking Gamma account...
                </Typography>
              )}
              {contactSelectionError && (
                <Typography variant="body3" color={theme.colors.text.error}>
                  Could not check this contact’s Gamma account.
                </Typography>
              )}
              {visibleUsers.length === 0 && contacts.length === 0 && isValidEmail && (
                <SearchOptionButton
                  type="button"
                  disabled={isSelectingContact}
                  onClick={handleSelectNewUser}
                >
                  <Typography variant="body2" color={alpha(theme.colors.text.primary, 0.6)}>
                    {search}
                  </Typography>
                </SearchOptionButton>
              )}
            </Stack>
          )}
        </CardContainer>
      )}

      {selectedUser && (
        <>
          <CardContainer>
            <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
              <Stack flexDirection="row" alignItems="center" gap="12px">
                <Avatar
                  initials={selectedUser.name ?? selectedUser.email}
                  src={selectedUser.avatarUrl}
                />
                <Stack flexDirection="column">
                  <Typography variant="body2" fontWeight={500}>
                    {selectedUser.name ?? selectedUser.email}
                  </Typography>
                  {selectedUser.name && (
                    <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
                      {selectedUser.email}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <Badge
                label={selectedUser.isNew ? 'New Investor' : 'Existing User'}
                color="primary"
                variant="subtle"
              />
            </Stack>
            <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body4" color={alpha(theme.colors.text.primary, 0.6)}>
                Current Introducer
              </Typography>
              <Typography
                variant="body3"
                color={
                  selectedUser.currentIntroducer
                    ? theme.colors.text.primary
                    : eligibility?.isEligible === false
                      ? theme.colors.text.error
                      : theme.colors.text.brand
                }
              >
                {selectedUser.currentIntroducer ??
                  (eligibility?.isEligible === false ? 'None – not eligible' : 'None – eligible')}
              </Typography>
            </Stack>

            {!selectedUser.isNew && eligibility && !eligibility.isEligible && (
              <InfoBanner severity="warning">
                <WarningIcon style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2 }} />
                <Typography variant="body4" color={alpha(theme.colors.text.primary, 0.6)}>
                  {eligibility.inBonusPeriodUntil
                    ? `This investor is in the new-user bonus period (until ${new Date(eligibility.inBonusPeriodUntil).toLocaleDateString()}). Attribution will be recorded but won't be active.`
                    : eligibility.activeFundIntroductionExpiresAt
                      ? `This investor already has an active fund introduction (expires ${new Date(eligibility.activeFundIntroductionExpiresAt).toLocaleDateString()}). Attribution will be recorded but won't be active.`
                      : 'This investor is currently not eligible for manual attribution.'}
                </Typography>
              </InfoBanner>
            )}

            {selectedUser.isNew && (
              <InfoBanner severity="warning">
                <InfoCircleIcon
                  color={theme.colors.icon.warning}
                  style={{ width: 20, height: 20, flexShrink: 0 }}
                />
                <Typography variant="body3">
                  A user with this email is not yet on Gamma Prime. They will receive an invitation
                  to sign-up if you proceed.
                </Typography>
              </InfoBanner>
            )}
          </CardContainer>

          <Stack gap="12px" marginTop="4px" flexDirection="column">
            <Typography variant="body2" fontWeight={600}>
              Attribution Type
            </Typography>

            <RadioGroup
              value={attributionType}
              onValueChange={(val) => onSelectAttributionType(val as AttributionTypeSelection)}
            >
              <Stack
                gap="8px"
                padding="12px"
                borderRadius="10px"
                sx={{ cursor: 'pointer' }}
                bgcolor={theme.colors.background.canvas}
                onClick={() => onSelectAttributionType('General')}
                flexDirection="column"
              >
                <Stack flexDirection="row" alignItems="center" gap="8px">
                  <Radio value="General" />
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={
                      attributionType === 'General'
                        ? theme.colors.text.brand
                        : theme.colors.text.primary
                    }
                  >
                    General Referral
                  </Typography>
                </Stack>
                <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
                  Attributes the user as a New Investor of this Introducer. The Introducer earns
                  fees on all their investments across the platform.
                </Typography>
              </Stack>

              <Stack
                gap="8px"
                padding="12px"
                borderRadius="10px"
                flexDirection="column"
                bgcolor={theme.colors.background.canvas}
                sx={{ cursor: 'pointer' }}
                onClick={() => onSelectAttributionType('Fund')}
              >
                <Stack flexDirection="row" alignItems="center" gap="8px">
                  <Radio value="Fund" />
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={
                      attributionType === 'Fund'
                        ? theme.colors.text.brand
                        : theme.colors.text.primary
                    }
                  >
                    Fund Introduction
                  </Typography>
                </Stack>
                <Typography variant="body3" color={alpha(theme.colors.text.primary, 0.6)}>
                  Introduces the user to a specific fund on behalf of this Introducer (if
                  applicable).
                </Typography>
              </Stack>
            </RadioGroup>
          </Stack>
        </>
      )}

      <FooterButtons>
        <Button color="tertiary" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button color="primary" variant="solid" disabled={!selectedUser} onClick={onNext}>
          Next
        </Button>
      </FooterButtons>
    </StepContainer>
  );
};
